'use client';

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { PanelLeftCloseIcon, PanelLeftOpenIcon } from 'lucide-react';

import { useIsMobile } from '../hooks';
import { cn, cva, VariantProps } from '../lib';

import { Button } from './button';
import { Input } from './input';
import { Separator } from './separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from './sheet';
import { Skeleton } from './skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip';

const SIDEBAR_COOKIE_NAME = 'sidebar_state';
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = '16rem';
const SIDEBAR_WIDTH_MOBILE = '18rem';
const SIDEBAR_WIDTH_ICON = '3.5rem';
const SIDEBAR_KEYBOARD_SHORTCUT = 'b';

type SidebarContextProps = {
  state: 'expanded' | 'collapsed';
  open: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
};

const SidebarContext = React.createContext<SidebarContextProps | null>(null);

function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider.');
  }

  return context;
}

/**
 * Provides context for the sidebar components, managing open/closed states.
 */
function SidebarProvider({
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  className,
  style,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  /** The default open state of the sidebar. */
  defaultOpen?: boolean;
  /** Controlled open state of the sidebar. */
  open?: boolean;
  /** Callback function triggered when the open state changes. */
  onOpenChange?: (open: boolean) => void;
}) {
  const isMobile = useIsMobile();
  const [openMobile, setOpenMobile] = React.useState(false);

  // This is the internal state of the sidebar.
  // We use openProp and setOpenProp for control from outside the component.
  const [_open, _setOpen] = React.useState(defaultOpen);
  const open = openProp ?? _open;
  const setOpen = React.useCallback(
    (value: boolean | ((value: boolean) => boolean)) => {
      const openState = typeof value === 'function' ? value(open) : value;
      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        _setOpen(openState);
      }

      // This sets the cookie to keep the sidebar state.
      document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
    },
    [setOpenProp, open]
  );

  const handleSidebarToggle = () => {
    if (isMobile) {
      setOpenMobile((isOpen) => !isOpen);
    } else {
      setOpen((isOpen) => !isOpen);
    }
  };

  // Helper to toggle the sidebar.
  const toggleSidebar = React.useCallback(handleSidebarToggle, [
    isMobile,
    setOpen,
    setOpenMobile,
  ]);

  // Adds a keyboard shortcut to toggle the sidebar.
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault();
        toggleSidebar();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleSidebar]);

  // We add a state so that we can do data-state="expanded" or "collapsed".
  // This makes it easier to style the sidebar with Tailwind classes.
  const state = open ? 'expanded' : 'collapsed';

  const contextValue = React.useMemo<SidebarContextProps>(
    () => ({
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
    }),
    [state, open, setOpen, isMobile, openMobile, setOpenMobile, toggleSidebar]
  );

  return (
    <SidebarContext.Provider value={contextValue}>
      <TooltipProvider delayDuration={0}>
        <div
          className={cn(
            `group/sidebar-wrapper has-data-[variant=inset]:bg-sidebar min-h-svh
            flex w-full bg-gray-50 dark:bg-neutral-900`,
            className
          )}
          data-slot="sidebar-wrapper"
          style={
            {
              '--sidebar-width': SIDEBAR_WIDTH,
              '--sidebar-width-icon': SIDEBAR_WIDTH_ICON,
              ...style,
            } as React.CSSProperties
          }
          {...props}
        >
          {children}
        </div>
      </TooltipProvider>
    </SidebarContext.Provider>
  );
}

/**
 * Root container for the sidebar, based on shadcn UI.
 */
function Sidebar({
  side = 'left',
  variant = 'sidebar',
  collapsible = 'offcanvas',
  className,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  /** The side from which the sidebar should appear. */
  side?: 'left' | 'right';
  /** The visual variant of the sidebar. */
  variant?: 'sidebar' | 'floating' | 'inset';
  /** How the sidebar should collapse. */
  collapsible?: 'offcanvas' | 'icon' | 'none';
}) {
  const { isMobile, state, openMobile, setOpenMobile } = useSidebar();

  if (collapsible === 'none') {
    return (
      <div
        className={cn(
          'bg-sidebar text-sidebar-foreground w-(--sidebar-width) flex h-full flex-col',
          className
        )}
        data-slot="sidebar"
        {...props}
      >
        {children}
      </div>
    );
  }

  if (isMobile) {
    return (
      <Sheet onOpenChange={setOpenMobile} open={openMobile} {...props}>
        <SheetContent
          className="bg-sidebar text-sidebar-foreground w-(--sidebar-width) p-0 [&>button]:hidden"
          data-mobile="true"
          data-sidebar="sidebar"
          data-slot="sidebar"
          side={side}
          style={
            {
              '--sidebar-width': SIDEBAR_WIDTH_MOBILE,
            } as React.CSSProperties
          }
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Sidebar</SheetTitle>
            <SheetDescription>Displays the mobile sidebar.</SheetDescription>
          </SheetHeader>
          <div className="flex h-full w-full flex-col">{children}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div
      className="text-sidebar-foreground group peer hidden md:block"
      data-collapsible={state === 'collapsed' ? collapsible : ''}
      data-side={side}
      data-slot="sidebar"
      data-state={state}
      data-variant={variant}
    >
      {/* This is what handles the sidebar gap on desktop */}
      <div
        className={cn(
          'w-(--sidebar-width) relative bg-transparent transition-[width] duration-200 ease-linear',
          'group-data-[collapsible=offcanvas]:w-0',
          'group-data-[side=right]:rotate-180',
          variant === 'floating' || variant === 'inset'
            ? 'group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]'
            : 'group-data-[collapsible=icon]:w-(--sidebar-width-icon)'
        )}
        data-slot="sidebar-gap"
      />
      <div
        className={cn(
          'h-svh w-(--sidebar-width) fixed inset-y-0 z-10 hidden transition-[left,right,width] duration-200 ease-linear md:flex',
          side === 'left'
            ? 'left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]'
            : 'right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]',
          // Adjust the padding for floating and inset variants.
          variant === 'floating' || variant === 'inset'
            ? 'p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]'
            : 'group-data-[collapsible=icon]:w-(--sidebar-width-icon) group-data-[side=left]:border-r group-data-[side=right]:border-l',
          className
        )}
        data-slot="sidebar-container"
        {...props}
      >
        <div
          className="
            bg-sidebar group-data-[variant=floating]:border-sidebar-border flex
            h-full w-full flex-col group-data-[variant=floating]:rounded-lg
            group-data-[variant=floating]:border group-data-[variant=floating]:shadow-sm
          "
          data-sidebar="sidebar"
          data-slot="sidebar-inner"
        >
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * The element that toggles the sidebar's open/collapsed state.
 */
function SidebarTrigger({
  className,
  onClick,
  ...props
}: React.ComponentProps<typeof Button>) {
  const { open: isOpen, toggleSidebar } = useSidebar();

  const ToggleIcon = isOpen ? PanelLeftCloseIcon : PanelLeftOpenIcon;

  return (
    <Button
      className={cn(
        'size-8 hover:bg-primary-50 dark:hover:bg-primary-800/40 group cursor-pointer transition-all duration-500',
        className
      )}
      data-sidebar="trigger"
      data-slot="sidebar-trigger"
      onClick={(event) => {
        onClick?.(event);
        toggleSidebar();
      }}
      size="icon"
      variant="ghost"
      {...props}
    >
      <ToggleIcon
        className="
          size-6 group-hover:text-primary-700 dark:group-hover:text-primary-300
          cursor-pointer stroke-1 text-gray-600 transition-all duration-500
          dark:text-gray-400
        "
      />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  );
}

/**
 * A handle element for resizing or toggling the sidebar.
 */
function SidebarRail({ className, ...props }: React.ComponentProps<'button'>) {
  const { toggleSidebar } = useSidebar();

  return (
    <button
      aria-label="Toggle Sidebar"
      className={cn(
        `hover:after:bg-sidebar-border in-data-[side=left]:cursor-w-resize
        in-data-[side=right]:cursor-e-resize hover:group-data-[collapsible=offcanvas]:bg-sidebar
        absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 transition-all
        ease-linear after:absolute after:inset-y-0 after:left-1/2 after:w-[2px]
        group-data-[side=left]:-right-4 group-data-[side=right]:left-0
        group-data-[collapsible=offcanvas]:translate-x-0
        group-data-[collapsible=offcanvas]:after:left-full
        sm:flex [[data-side=left][data-collapsible=offcanvas]_&]:-right-2
        [[data-side=left][data-state=collapsed]_&]:cursor-e-resize
        [[data-side=right][data-collapsible=offcanvas]_&]:-left-2
        [[data-side=right][data-state=collapsed]_&]:cursor-w-resize`,
        className
      )}
      data-sidebar="rail"
      data-slot="sidebar-rail"
      onClick={toggleSidebar}
      tabIndex={-1}
      title="Toggle Sidebar"
      type="button"
      {...props}
    />
  );
}

/**
 * The main content area that sits alongside the sidebar.
 */
function SidebarInset({ className, ...props }: React.ComponentProps<'main'>) {
  const { open: isOpen } = useSidebar();

  return (
    <main
      className={cn(
        `min-h-svh relative isolate flex w-full flex-1 flex-col bg-gray-50 pb-12 dark:bg-neutral-900
        md:peer-data-[variant=inset]:m-2 md:peer-data-[variant=inset]:ms-0
        md:peer-data-[variant=inset]:peer-data-[state=collapsed]:ms-2
        md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow-sm `,
        !isOpen &&
          'border-neutral-200/80 border-s-[0.5] dark:border-neutral-800/70',
        className
      )}
      data-slot="sidebar-inset"
      {...props}
    />
  );
}

/**
 * An input field within the sidebar.
 */
function SidebarInput({
  className,
  ...props
}: React.ComponentProps<typeof Input>) {
  return (
    <Input
      className={cn('bg-background h-8 w-full shadow-none', className)}
      data-sidebar="input"
      data-slot="sidebar-input"
      {...props}
    />
  );
}

/**
 * The header section of the sidebar.
 */
function SidebarHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn('flex flex-col gap-2 p-2', className)}
      data-sidebar="header"
      data-slot="sidebar-header"
      {...props}
    />
  );
}

/**
 * The footer section of the sidebar.
 */
function SidebarFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn('flex flex-col gap-2 p-2', className)}
      data-sidebar="footer"
      data-slot="sidebar-footer"
      {...props}
    />
  );
}

/**
 * A horizontal divider within the sidebar.
 */
function SidebarSeparator({
  className,
  ...props
}: React.ComponentProps<typeof Separator>) {
  return (
    <Separator
      className={cn('bg-sidebar-border mx-2 w-auto', className)}
      data-sidebar="separator"
      data-slot="sidebar-separator"
      {...props}
    />
  );
}

/**
 * The main body content area of the sidebar.
 */
function SidebarContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'flex min-h-0 flex-1 flex-col gap-2 overflow-auto group-data-[collapsible=icon]:overflow-hidden',
        className
      )}
      data-sidebar="content"
      data-slot="sidebar-content"
      {...props}
    />
  );
}

/**
 * A section for grouping related sidebar content.
 */
function SidebarGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn('relative flex w-full min-w-0 flex-col p-2', className)}
      data-sidebar="group"
      data-slot="sidebar-group"
      {...props}
    />
  );
}

/**
 * The label for a sidebar group.
 */
function SidebarGroupLabel({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<'div'> & { 
  /** Whether to render the child component as the label. */
  asChild?: boolean 
}) {
  const Comp = asChild ? Slot : 'div';

  return (
    <Comp
      className={cn(
        `text-sidebar-foreground/70 ring-sidebar-ring outline-hidden [&>svg]:size-5
        flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium
        transition-[margin,opacity] duration-200 ease-linear focus-visible:ring-2
        group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0
        [&>svg]:shrink-0`,
        className
      )}
      data-sidebar="group-label"
      data-slot="sidebar-group-label"
      {...props}
    />
  );
}

/**
 * An action button within a sidebar group header.
 */
function SidebarGroupAction({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> & { 
  /** Whether to render the child component as the action button. */
  asChild?: boolean 
}) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      className={cn(
        `text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent
        hover:text-sidebar-accent-foreground outline-hidden [&>svg]:size-5
        absolute right-3 top-3.5 flex aspect-square w-5 items-center
        justify-center rounded-md p-0 transition-transform
        focus-visible:ring-2 [&>svg]:shrink-0
        `,
        // Increases the hit area of the button on mobile.
        'after:absolute after:-inset-2 md:after:hidden',
        'group-data-[collapsible=icon]:hidden',
        className
      )}
      data-sidebar="group-action"
      data-slot="sidebar-group-action"
      {...props}
    />
  );
}

/**
 * The content container for a sidebar group.
 */
function SidebarGroupContent({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn('w-full text-sm', className)}
      data-sidebar="group-content"
      data-slot="sidebar-group-content"
      {...props}
    />
  );
}

/**
 * The main menu container within the sidebar.
 */
function SidebarMenu({ className, ...props }: React.ComponentProps<'ul'>) {
  return (
    <ul
      className={cn(
        'flex w-full min-w-0 flex-col gap-4 group-data-[collapsible=icon]:items-center',
        className
      )}
      data-sidebar="menu"
      data-slot="sidebar-menu"
      {...props}
    />
  );
}

/**
 * An individual item within a sidebar menu.
 */
function SidebarMenuItem({ className, ...props }: React.ComponentProps<'li'>) {
  return (
    <li
      className={cn('group/menu-item relative w-full', className)}
      data-sidebar="menu-item"
      data-slot="sidebar-menu-item"
      {...props}
    />
  );
}

const sidebarMenuButtonVariants = cva(
  `peer/menu-button flex w-full items-center gap-3 overflow-hidden rounded-md px-2 py-2.5
  text-start text-sm outline-hidden ring-sidebar-ring transition-[width,height,padding]
  hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2
  active:bg-sidebar-accent active:text-sidebar-accent-foreground
  disabled:pointer-events-none disabled:opacity-50 group-has-data-[sidebar=menu-action]/menu-item:pr-8
  aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent
  data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground
  data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground
  group-data-[collapsible=icon]:size-10! group-data-[collapsible=icon]:p-2.5!
  group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:[&>*:not(:first-child)]:hidden
  [&>span:last-child]:truncate [&>svg]:size-5 [&>svg]:shrink-0 cursor-pointer
  `,
  {
    variants: {
      variant: {
        default: 'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
        outline: `bg-background shadow-[0_0_0_1px_hsl(var(--sidebar-border))]
          hover:bg-sidebar-accent hover:text-sidebar-accent-foreground
          hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]
          `,
      },
      size: {
        default: 'h-9 text-sm',
        sm: 'h-7 text-xs',
        lg: 'h-12 text-sm group-data-[collapsible=icon]:p-0!',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

/**
 * The button that represents a menu item in the sidebar.
 */
function SidebarMenuButton({
  asChild = false,
  isActive = false,
  variant = 'default',
  size = 'default',
  tooltip: tooltipProps,
  className,
  ...props
}: React.ComponentProps<'button'> & {
  /** Whether to render the child component as the button. */
  asChild?: boolean;
  /** Whether this menu item is currently active. */
  isActive?: boolean;
  /** Tooltip content to show when the sidebar is collapsed. */
  tooltip?: string | React.ComponentProps<typeof TooltipContent>;
} & VariantProps<typeof sidebarMenuButtonVariants>) {
  let tooltip = tooltipProps;
  const Comp = asChild ? Slot : 'button';
  const { isMobile, state } = useSidebar();

  const button = (
    <Comp
      className={cn(sidebarMenuButtonVariants({ variant, size }), className)}
      data-active={isActive}
      data-sidebar="menu-button"
      data-size={size}
      data-slot="sidebar-menu-button"
      {...props}
    />
  );

  if (!tooltip) {
    return button;
  }

  if (typeof tooltip === 'string') {
    tooltip = {
      children: tooltip,
    };
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent
        align="center"
        hidden={state !== 'collapsed' || isMobile}
        side="right"
        {...tooltip}
      />
    </Tooltip>
  );
}

/**
 * An action button associated with a menu item in the sidebar.
 */
function SidebarMenuAction({
  className,
  asChild = false,
  showOnHover = false,
  ...props
}: React.ComponentProps<'button'> & {
  /** Whether to render the child component as the action button. */
  asChild?: boolean;
  /** Whether to show the action button only when the menu item is hovered. */
  showOnHover?: boolean;
}) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      className={cn(
        `text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent
        hover:text-sidebar-accent-foreground peer-hover/menu-button:text-sidebar-accent-foreground
        outline-hidden [&>svg]:size-5 absolute right-1 top-1.5
        flex aspect-square w-5 items-center justify-center rounded-md
        p-0 transition-transform
        focus-visible:ring-2 [&>svg]:shrink-0
        `,
        // Increases the hit area of the button on mobile.
        'after:absolute after:-inset-2 md:after:hidden',
        'peer-data-[size=sm]/menu-button:top-1',
        'peer-data-[size=default]/menu-button:top-1.5',
        'peer-data-[size=lg]/menu-button:top-2.5',
        'group-data-[collapsible=icon]:hidden',
        showOnHover &&
          `peer-data-[active=true]/menu-button:text-sidebar-accent-foreground
          group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100
          data-[state=open]:opacity-100 md:opacity-0`,
        className
      )}
      data-sidebar="menu-action"
      data-slot="sidebar-menu-action"
      {...props}
    />
  );
}

/**
 * A badge to display next to a menu item in the sidebar.
 */
function SidebarMenuBadge({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        `text-sidebar-foreground min-w-5 peer-hover/menu-button:text-sidebar-accent-foreground
        peer-data-[active=true]/menu-button:text-sidebar-accent-foreground
        pointer-events-none absolute right-1 flex h-5 select-none items-center
        justify-center rounded-md px-1 text-xs font-medium tabular-nums
        group-data-[collapsible=icon]:hidden peer-data-[size=default]/menu-button:top-1.5
        peer-data-[size=lg]/menu-button:top-2.5 peer-data-[size=sm]/menu-button:top-1`,
        className
      )}
      data-sidebar="menu-badge"
      data-slot="sidebar-menu-badge"
      {...props}
    />
  );
}

/**
 * A loading skeleton for a sidebar menu item.
 */
function SidebarMenuSkeleton({
  className,
  showIcon = false,
  ...props
}: React.ComponentProps<'div'> & {
  /** Whether to show a skeleton for the icon as well. */
  showIcon?: boolean;
}) {
  // Random width between 50 to 90%.
  const width = React.useMemo(
    () => `${Math.floor(Math.random() * 40) + 50}%`,
    []
  );

  return (
    <div
      className={cn('flex h-8 items-center gap-2 rounded-md px-2', className)}
      data-sidebar="menu-skeleton"
      data-slot="sidebar-menu-skeleton"
      {...props}
    >
      {showIcon && (
        <Skeleton
          className="size-4 rounded-md"
          data-sidebar="menu-skeleton-icon"
        />
      )}
      <Skeleton
        className="max-w-(--skeleton-width) h-4 flex-1"
        data-sidebar="menu-skeleton-text"
        style={
          {
            '--skeleton-width': width,
          } as React.CSSProperties
        }
      />
    </div>
  );
}

/**
 * A sub-menu container within a sidebar menu item.
 */
function SidebarMenuSub({ className, ...props }: React.ComponentProps<'ul'>) {
  return (
    <ul
      className={cn(
        `border-sidebar-border mx-3.5 flex min-w-0 translate-x-px flex-col gap-1
        border-l px-2.5 py-0.5 group-data-[collapsible=icon]:hidden`,
        className
      )}
      data-sidebar="menu-sub"
      data-slot="sidebar-menu-sub"
      {...props}
    />
  );
}

/**
 * An individual item within a sidebar sub-menu.
 */
function SidebarMenuSubItem({
  className,
  ...props
}: React.ComponentProps<'li'>) {
  return (
    <li
      className={cn('group/menu-sub-item relative', className)}
      data-sidebar="menu-sub-item"
      data-slot="sidebar-menu-sub-item"
      {...props}
    />
  );
}

/**
 * The button that represents a sub-menu item in the sidebar.
 */
function SidebarMenuSubButton({
  asChild = false,
  size = 'md',
  isActive = false,
  className,
  ...props
}: React.ComponentProps<'a'> & {
  /** Whether to render the child component as the button. */
  asChild?: boolean;
  /** The size variant of the sub-menu button. */
  size?: 'sm' | 'md';
  /** Whether this sub-menu item is currently active. */
  isActive?: boolean;
}) {
  const Comp = asChild ? Slot : 'a';

  return (
    <Comp
      className={cn(
        `text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent
        hover:text-sidebar-accent-foreground active:bg-sidebar-accent
        active:text-sidebar-accent-foreground [&>svg]:text-sidebar-accent-foreground
        outline-hidden [&>svg]:size-5 flex h-7 min-w-0 -translate-x-px items-center
        gap-2 overflow-hidden rounded-md px-2 focus-visible:ring-2
        disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none
        aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:shrink-0`,
        'data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground',
        size === 'sm' && 'text-xs',
        size === 'md' && 'text-sm',
        'group-data-[collapsible=icon]:hidden',
        className
      )}
      data-active={isActive}
      data-sidebar="menu-sub-button"
      data-size={size}
      data-slot="sidebar-menu-sub-button"
      {...props}
    />
  );
}

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
};
